import { StyleSheet, Text, View } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { Diamond, Award, Medal, Shield, Circle } from 'lucide-react-native';

export type Division = {
  name: string;
  color: string;
  description: string;
  maxPercentile: number;
};

export const DIVISIONS: Division[] = [
  { name: 'Diamond', color: '#b9f2ff', description: 'Elite', maxPercentile: 850 },
  { name: 'Platinum', color: '#e5e4e2', description: 'Advanced', maxPercentile: 750 },
  { name: 'Gold', color: '#ffd700', description: 'Intermediate', maxPercentile: 650 },
  { name: 'Silver', color: '#c0c0c0', description: 'Novice', maxPercentile: 550 },
  { name: 'Bronze', color: '#cd7f32', description: 'Beginner', maxPercentile: 400 },
  { name: 'Iron', color: '#71797E', description: 'Getting Started', maxPercentile: 0 },
];

/**
 * Get division based on ABSOLUTE strength score, not relative percentile.
 * This ensures divisions are meaningful regardless of user pool size.
 * 
 * Score ranges (V1 scoring × 100, unverified 0.7x):
 *   Iron:     0 - 399    (just getting started)
 *   Bronze:   400 - 549  (beginner lifter)
 *   Silver:   550 - 649  (novice, building a foundation)
 *   Gold:     650 - 749  (intermediate, solid strength)
 *   Platinum: 750 - 849  (advanced, serious lifter)
 *   Diamond:  850+       (elite / near world-class)
 */
export function getDivision(rank: number, total: number, score?: number): Division {
  // Use absolute score thresholds if score is provided
  if (score != null && score > 0) {
    if (score >= 850) return DIVISIONS[0]; // Diamond
    if (score >= 750) return DIVISIONS[1]; // Platinum
    if (score >= 650) return DIVISIONS[2]; // Gold
    if (score >= 550) return DIVISIONS[3]; // Silver
    if (score >= 400) return DIVISIONS[4]; // Bronze
    return DIVISIONS[5]; // Iron
  }
  // Fallback to percentile-based (for backward compat)
  if (total === 0) return DIVISIONS[5];
  const pct = (rank / total) * 100;
  if (pct <= 1) return DIVISIONS[0];
  if (pct <= 5) return DIVISIONS[1];
  if (pct <= 15) return DIVISIONS[2];
  if (pct <= 30) return DIVISIONS[3];
  if (pct <= 50) return DIVISIONS[4];
  return DIVISIONS[5];
}

type Props = {
  divisionName: string;
  color: string;
  description: string;
};

const ICON_MAP: Record<string, React.ComponentType<any>> = {
  Diamond,
  Platinum: Award,
  Gold: Medal,
  Silver: Medal,
  Bronze: Shield,
  Iron: Circle,
};

export default function RankDivisionBand({ divisionName, color, description }: Props) {
  const { theme } = useTheme();
  const Icon = ICON_MAP[divisionName] ?? Circle;

  return (
    <View style={[styles.band, { backgroundColor: color + '18' }]}>
      <View style={[styles.line, { backgroundColor: color }]} />
      <Icon size={12} color={color} />
      <Text style={[styles.label, { color }]}>{divisionName}</Text>
      <Text style={[styles.desc, { color: theme.colors.textSecondary }]}>{description}</Text>
      <View style={[styles.line, { backgroundColor: color, flex: 1 }]} />
    </View>
  );
}

const styles = StyleSheet.create({
  band: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 4,
    paddingHorizontal: 12,
    marginVertical: 2,
    borderRadius: 4,
  },
  line: { height: 1, width: 16 },
  label: { fontSize: 11, fontWeight: '800', textTransform: 'uppercase', letterSpacing: 0.5 },
  desc: { fontSize: 11, fontWeight: '500' },
});
